import type { ClientTracker, TrackerEntry } from '@/types/trackers';

const K_TRACKERS = 'coachd-client-trackers';
const K_ENTRIES = 'coachd-tracker-entries';

function read<T>(k: string, d: T): T {
  try {
    return (JSON.parse(localStorage.getItem(k) || 'null') ?? d) as T;
  } catch {
    return d;
  }
}
function write(k: string, v: any) {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    // OBS-11 a-ignore: localStorage throws in private mode / when full; this is a best-effort cache and the app works without it.
  }
}

export const localTrackersStore = {
  async listTrackers(clientId: string): Promise<ClientTracker[]> {
    const rows = read<ClientTracker[]>(K_TRACKERS, []);
    return rows.filter((t) => String(t.clientId) === String(clientId) && !t.isArchived);
  },
  async upsertTracker(row: ClientTracker): Promise<void> {
    const rows = read<ClientTracker[]>(K_TRACKERS, []);
    const idx = rows.findIndex((r) => r.id === row.id);
    if (idx >= 0) rows[idx] = row;
    else rows.push(row);
    write(K_TRACKERS, rows);
  },
  async deleteTracker(trackerId: string): Promise<void> {
    const rows = read<ClientTracker[]>(K_TRACKERS, []);
    write(
      K_TRACKERS,
      rows.filter((r) => r.id !== trackerId),
    );
  },
  async listEntries(trackerId: string): Promise<TrackerEntry[]> {
    const map = read<Record<string, TrackerEntry[]>>(K_ENTRIES, {});
    return map[trackerId] ?? [];
  },
  async addEntry(entry: TrackerEntry): Promise<void> {
    const map = read<Record<string, TrackerEntry[]>>(K_ENTRIES, {});
    const list = map[entry.trackerId] ?? [];
    list.push(entry);
    map[entry.trackerId] = list.slice(-2000);
    write(K_ENTRIES, map);
    try {
      const BC = new BroadcastChannel('coachd-trackers');
      BC.postMessage({ type: 'tracker_entries_changed', trackerId: String(entry.trackerId) });
    } catch {
      // OBS-11 a-ignore: BroadcastChannel is missing in some browsers and throws once closed; cross-tab sync is an enhancement, polling still refreshes.
    }
  },
  async listEntriesForClient(clientId: string, _coachId?: string): Promise<TrackerEntry[]> {
    const map = read<Record<string, TrackerEntry[]>>(K_ENTRIES, {});
    const out: TrackerEntry[] = [];
    for (const [, rows] of Object.entries(map)) {
      for (const r of rows || []) {
        if (String((r as any).clientId) === String(clientId)) {
          out.push({ ...(r as any), trackerId: String((r as any).trackerId) } as TrackerEntry);
        }
      }
    }
    out.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
    return out;
  },
};

export type TrackersStore = typeof localTrackersStore;
